import React, { useCallback, useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import apiClient from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { avisarRevisionesActualizadas } from '../hooks/useRevisionesPendientes';

// 21 sep 2026 (Augusto: "debería permitir importar todo a PCM pero una
// vez dentro, colocar alertas (con indicadores numéricos) de los
// problemas encontrados que se tienen que solucionar").
//
// Acá se resuelve lo que el importador dejó entrar marcado. Dos motivos
// hoy:
//
// - posible_duplicado: se parece a algo que ya estaba registrado. Se
//   muestra CONTRA QUÉ choca, que es lo que permite decidir sin ir a
//   buscarlo a otra pantalla.
// - falta_origen: un pago a una tarjeta del que no sabemos de qué cuenta
//   salió. Hasta completarlo no se puede armar la transferencia real, y
//   registrarlo como ingreso suelto inflaría los ingresos del mes.
//
// Mientras algo esté acá, no suma en ningún total. Eso se dice en la
// pantalla, arriba, porque es la única forma de que el número del
// tablero se entienda: si alguien ve 3 pendientes y su gasto del mes le
// parece bajo, tiene que poder conectar las dos cosas.

const MOTIVOS = {
  posible_duplicado: {
    es: 'Posible duplicado',
    en: 'Possible duplicate',
    color: 'warning',
  },
  falta_origen: {
    es: 'Falta la cuenta de origen',
    en: 'Missing source account',
    color: 'info',
  },
  // 24 sep 2026 (Augusto: "colocar como pendiente en el caso se piense
  // que es un cambio de moneda").
  posible_cambio_moneda: {
    es: '¿Es un cambio de moneda?',
    en: 'Is this a currency exchange?',
    color: 'warning',
  },
  // 30 oct 2026 (Augusto: "todos los primeros o segundos días del mes se
  // pagan tarjetas de crédito al 100%"). PCM lo prepara el día de pago
  // configurado en Deudas; tú confirmas que se hizo.
  pago_programado: {
    es: 'Pago de tarjeta programado',
    en: 'Scheduled card payment',
    color: 'success',
  },
};

const RevisionPage = () => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [resolviendo, setResolviendo] = useState(null); // id en curso
  const [cuentas, setCuentas] = useState([]);
  const [origenElegido, setOrigenElegido] = useState({}); // { [txId]: cuentaId }
  // 25 sep 2026: cuánto salió de la cuenta de origen, cuando esa cuenta
  // está en otra moneda que el pago. { [txId]: string }
  const [montoOrigen, setMontoOrigen] = useState({});
  // 30 oct 2026: lo que de verdad se pagó, si no fue el total preparado.
  // { [txId]: string }
  const [montoPagado, setMontoPagado] = useState({});

  // 28 sep 2026 (Augusto: "añadir la opción de añadir cuenta nueva, sea
  // deuda o cuenta bancaria, en la zona de Revisión pendiente").
  //
  // El desplegable solo ofrecía las cuentas ya registradas. Si el pago
  // salió de una cuenta que todavía no está en PCM --lo normal cuando
  // recién importas tarjetas y aún no las cuentas del banco-- no había
  // salida: o lo dejabas pendiente para siempre, o te ibas a Cuentas,
  // creabas la cuenta y volvías, perdiendo de vista cuál de los diez
  // movimientos estabas resolviendo.
  //
  // 28 sep 2026 (Augusto: "la nueva deuda que añadí no aparece en la
  // sección de deudas"). Dos cosas fallaban: "otro" no estaba en la lista
  // del backend que crea la deuda espejo (corregido allá), y esta lista
  // decía "Otro" a secas -- sin decir si eso era otra cuenta bancaria u
  // otra deuda. Ahora cada opción dice a dónde va a parar.
  const TIPOS_NUEVA_CUENTA = [
    { value: 'ahorros', label: isSpanish ? 'Cuenta de Ahorros' : 'Savings Account' },
    { value: 'corriente', label: isSpanish ? 'Cuenta Corriente' : 'Checking Account' },
    { value: 'tarjeta_credito', label: isSpanish ? 'Tarjeta de Crédito' : 'Credit Card' },
    { value: 'prestamo_personal', label: isSpanish ? 'Préstamo Personal' : 'Personal Loan' },
    { value: 'prestamo_hipotecario', label: isSpanish ? 'Préstamo Hipotecario' : 'Mortgage Loan' },
    { value: 'otro', label: isSpanish ? 'Otra deuda' : 'Other debt' },
  ];
  const esTipoDeuda = (tipo) => !['ahorros', 'corriente'].includes(tipo);
  const [openNuevaCuenta, setOpenNuevaCuenta] = useState(false);
  const [creandoCuenta, setCreandoCuenta] = useState(false);
  const [errorNuevaCuenta, setErrorNuevaCuenta] = useState(null);
  // A qué movimiento se le va a asignar la cuenta recién creada.
  const [txParaNuevaCuenta, setTxParaNuevaCuenta] = useState(null);
  const [nuevaCuenta, setNuevaCuenta] = useState({
    nombre: '', tipo: 'ahorros', entidad: '', moneda: 'PEN', saldo: '',
  });
  const abrirNuevaCuenta = (m) => {
    setTxParaNuevaCuenta(m.id);
    setNuevaCuenta({
      nombre: '',
      // 28 sep 2026 (Augusto: "Préstamo Mamá se ve en Transacciones pero
      // no en Deuda"). Arrancaba en 'ahorros': escribías "Préstamo Mamá",
      // no tocabas el desplegable, y salía una cuenta de ahorros. El
      // nombre decía préstamo y PCM guardaba otra cosa.
      //
      // Ahora arranca vacío y el botón Crear no se habilita hasta
      // elegirlo. Un default silencioso en un campo que decide DÓNDE va a
      // aparecer la cuenta es una trampa.
      tipo: '',
      entidad: '',
      // Arranca en la moneda del pago: si salió US$ 3,771, la cuenta de
      // origen casi seguro está en dólares.
      moneda: (m.moneda || 'PEN').toUpperCase(),
      saldo: '',
    });
    setErrorNuevaCuenta(null);
    setOpenNuevaCuenta(true);
  };
  const crearCuenta = async () => {
    if (!nuevaCuenta.nombre.trim() || !nuevaCuenta.tipo) return;
    setCreandoCuenta(true);
    setErrorNuevaCuenta(null);
    try {
      // Mismo endpoint que usa Cuentas. Si el tipo es de deuda, el
      // backend crea además su entrada en Deudas (ver AccountCreate en
      // schemas/account.py), así que no hay dos caminos que mantener.
      const esDeuda = esTipoDeuda(nuevaCuenta.tipo);
      const monto = parseFloat(nuevaCuenta.saldo) || 0;
      const { data } = await apiClient.post('/dashboard/accounts', {
        nombre: nuevaCuenta.nombre.trim(),
        tipo: nuevaCuenta.tipo,
        entidad: nuevaCuenta.entidad.trim() || null,
        moneda: nuevaCuenta.moneda,
        // Una deuda se guarda en negativo, igual que en Cuentas.
        saldo: esDeuda ? -Math.abs(monto) : monto,
      });
      const { data: lista } = await apiClient.get('/dashboard/accounts');
      setCuentas(lista || []);
      // Queda elegida en el movimiento desde el que se abrió: es para lo
      // que se creó.
      if (txParaNuevaCuenta && data?.id) {
        setOrigenElegido((p) => ({ ...p, [txParaNuevaCuenta]: data.id }));
      }
      setOpenNuevaCuenta(false);
    } catch (err) {
      setErrorNuevaCuenta(
        err.response?.data?.detail ||
          (isSpanish ? 'No se pudo crear la cuenta.' : 'Could not create the account.')
      );
    } finally {
      setCreandoCuenta(false);
    }
  };

  const cargar = useCallback(() => {
    setCargando(true);
    Promise.all([
      apiClient.get('/transactions/pendientes'),
      apiClient.get('/dashboard/accounts').catch(() => ({ data: [] })),
    ])
      .then(([resPend, resCuentas]) => {
        setDatos(resPend.data);
        setCuentas(resCuentas.data || []);
        setError(null);
        // 22 sep 2026 (Augusto: "cuando se vea Pago de tarjeta, se debe
        // de reconocer que ese pago se hizo de una cuenta sea esta de
        // ahorros o cuenta corriente"). Para cada pago sin origen se le
        // pide al backend la cuenta más probable --la que ya usaste para
        // pagar esa tarjeta-- y queda preseleccionada. Se propone, no se
        // decide: un pago atribuido a la cuenta equivocada deja dos
        // saldos mal y nada a la vista que lo explique.
        (resPend.data?.movimientos || [])
          .filter((m) => m.revision_motivo === 'falta_origen' && m.contexto?.id)
          .forEach((m) => {
            apiClient
              .get('/transactions/sugerir-origen-pago', {
                params: { account_id: m.contexto.id, moneda: m.moneda || undefined },
              })
              .then((r) => {
                const sugerida = r.data?.cuenta_origen_id;
                if (!sugerida) return;
                setOrigenElegido((prev) => (prev[m.id] ? prev : { ...prev, [m.id]: sugerida }));
              })
              .catch(() => {});
          });
      })
      .catch(() => setError(isSpanish ? 'No se pudo cargar la revisión.' : 'Could not load the review.'))
      .finally(() => setCargando(false));
  }, [isSpanish]);

  useEffect(cargar, [cargar]);

  const resolver = async (tx, accion) => {
    setResolviendo(tx.id);
    setError(null);
    try {
      await apiClient.post(`/transactions/${tx.id}/resolver`, {
        accion,
        ...(accion === 'origen'
          ? {
              cuenta_origen_id: origenElegido[tx.id],
              // 25 sep 2026: solo se manda cuando de verdad hay cambio de
              // moneda entre el pago y la cuenta de origen. Si coinciden,
              // el backend usa el mismo monto en los dos lados y este
              // campo ni se pregunta (ver montoOrigenNecesario).
              ...(montoOrigen[tx.id] ? { monto_origen: parseFloat(montoOrigen[tx.id]) } : {}),
            }
          : {}),
        ...(accion === 'confirmar_pago' && montoPagado[tx.id] && parseFloat(montoPagado[tx.id]) !== Number(tx.monto)
          ? { monto: parseFloat(montoPagado[tx.id]) }
          : {}),
      });
      setMontoOrigen((p) => ({ ...p, [tx.id]: '' }));
      avisarRevisionesActualizadas();
      cargar();
    } catch (err) {
      setError(err.response?.data?.detail || (isSpanish ? 'No se pudo resolver.' : 'Could not resolve.'));
    } finally {
      setResolviendo(null);
    }
  };

  // 30 oct 2026 (Augusto, con 91 pendientes tras reimportar el BBVA:
  // "qué botón del aviso... no me permite clickearlo"). Borra de una vez
  // los duplicados que son copia exacta (misma cuenta, monto, moneda y
  // día). Los que chocan con algo en OTRA cuenta se quedan: ahí hay que
  // decidir.
  const [limpiando, setLimpiando] = useState(false);
  const [resultadoLimpieza, setResultadoLimpieza] = useState(null);
  const limpiarExactos = async (cuantos) => {
    if (!window.confirm(
      isSpanish
        ? `Se van a borrar ${cuantos} movimiento(s) que son copia exacta de algo que ya tienes (misma cuenta, monto, moneda y fecha). Lo que ya estaba registrado no se toca. ¿Seguir?`
        : `${cuantos} exact copies will be deleted. Continue?`
    )) return;
    setLimpiando(true);
    setError(null);
    try {
      const { data } = await apiClient.post('/transactions/pendientes/descartar-duplicados-exactos');
      setResultadoLimpieza(data);
      avisarRevisionesActualizadas();
      cargar();
    } catch (err) {
      setError(err.response?.data?.detail || (isSpanish ? 'No se pudo completar. Revisa tu conexión e inténtalo de nuevo.' : 'Could not complete.'));
    } finally {
      setLimpiando(false);
    }
  };

  const formatoMonto = (m) => {
    const simbolo = m.moneda === 'USD' ? 'US$' : 'S/';
    return `${simbolo} ${Number(m.monto || 0).toFixed(2)}`;
  };

  if (cargando && !datos) {
    return (
      <Container maxWidth="md" sx={{ pt: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const movimientos = datos?.movimientos || [];

  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {isSpanish ? 'Revisión pendiente' : 'Pending review'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {movimientos.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            {isSpanish ? 'No hay nada pendiente' : 'Nothing pending'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSpanish
              ? 'Todos tus movimientos están contando en tus totales.'
              : 'All your transactions are counting toward your totals.'}
          </Typography>
        </Paper>
      ) : (
        <>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {isSpanish
              ? `${movimientos.length} movimiento(s) entraron a PCM pero esperan una decisión. Mientras estén acá NO suman en tu tablero, presupuestos ni informes — así tus números no quedan mal por algo sin revisar.`
              : `${movimientos.length} transaction(s) are in PCM but awaiting a decision. While they're here they do NOT count toward your dashboard, budgets or reports — so your numbers aren't wrong because of something unreviewed.`}
          </Alert>

          {(() => {
            const exactos = movimientos.filter(
              (m) => m.revision_motivo === 'posible_duplicado' && m.contexto?.exacto
            ).length;
            if (exactos === 0) return null;
            return (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 260 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {isSpanish
                      ? `${exactos} son copia exacta de algo que ya tienes`
                      : `${exactos} are exact copies of something you already have`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isSpanish
                      ? 'Misma cuenta, monto, moneda y fecha: suele pasar al volver a importar un extracto. Se pueden borrar todos juntos; lo que ya estaba registrado se queda.'
                      : 'Same account, amount, currency and date. They can all be deleted at once.'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  disabled={limpiando}
                  onClick={() => limpiarExactos(exactos)}
                >
                  {limpiando
                    ? (isSpanish ? 'Borrando...' : 'Deleting...')
                    : (isSpanish ? `Borrar los ${exactos} duplicados` : `Delete ${exactos} duplicates`)}
                </Button>
              </Paper>
            );
          })()}
          {resultadoLimpieza && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResultadoLimpieza(null)}>
              {isSpanish
                ? `Listo: se borraron ${resultadoLimpieza.borrados}. Quedan ${resultadoLimpieza.quedan} por revisar.`
                : `Done: ${resultadoLimpieza.borrados} deleted, ${resultadoLimpieza.quedan} left.`}
            </Alert>
          )}

          {movimientos.map((m) => {
            const motivo = MOTIVOS[m.revision_motivo] || {};
            const esFaltaOrigen = m.revision_motivo === 'falta_origen';
            const esPosibleCambio = m.revision_motivo === 'posible_cambio_moneda';
            const esPagoProgramado = m.revision_motivo === 'pago_programado';
            return (
              <Paper key={m.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 220 }}>
                    <Chip
                      size="small"
                      color={motivo.color || 'default'}
                      label={isSpanish ? motivo.es : motivo.en}
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="subtitle1" fontWeight={600}>
                      {formatoMonto(m)}
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        {m.fecha ? m.fecha.slice(0, 10) : ''}
                      </Typography>
                    </Typography>
                    <Typography variant="body2">
                      {m.proveedor || m.descripcion || (isSpanish ? 'Sin descripción' : 'No description')}
                    </Typography>
                    {m.categoria && (
                      <Typography variant="caption" color="text.secondary">{m.categoria}</Typography>
                    )}
                  </Box>

                  {/* El contexto es lo que hace posible decidir sin salir
                      de acá: con qué choca, o qué tarjeta se estaba
                      pagando. */}
                  {m.contexto?.tipo === 'transaccion' && (
                    <Box sx={{ flex: 1, minWidth: 220, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {isSpanish ? 'Ya tienes registrada:' : 'Already recorded:'}
                      </Typography>
                      <Typography variant="body2">
                        {m.contexto.fecha?.slice(0, 10)} · {formatoMonto(m.contexto)}
                        {m.contexto.proveedor ? ` · ${m.contexto.proveedor}` : ''}
                      </Typography>
                      {m.contexto.cuenta && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', fontWeight: m.contexto.misma_cuenta ? 400 : 700, color: m.contexto.misma_cuenta ? 'text.secondary' : 'warning.dark' }}
                        >
                          {isSpanish ? 'En: ' : 'In: '}{m.contexto.cuenta}
                          {!m.contexto.misma_cuenta && (isSpanish ? ' — otra cuenta' : ' — a different account')}
                        </Typography>
                      )}
                      {m.contexto.descripcion && (
                        <Typography variant="caption" color="text.secondary">
                          {m.contexto.descripcion}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {m.contexto?.tipo === 'posible_cambio' && (
                    <Box sx={{ flex: 1, minWidth: 220, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {isSpanish ? 'Y el mismo día, en otra cuenta:' : 'And the same day, in another account:'}
                      </Typography>
                      <Typography variant="body2">
                        {m.contexto.fecha?.slice(0, 10)} · {formatoMonto(m.contexto)}
                        {m.contexto.cuenta ? ` · ${m.contexto.cuenta}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {m.contexto.tipo_movimiento === 'gasto'
                          ? (isSpanish ? 'salió de esa cuenta' : 'left that account')
                          : (isSpanish ? 'entró a esa cuenta' : 'arrived in that account')}
                      </Typography>
                    </Box>
                  )}
                  {m.contexto?.tipo === 'pago_programado' && (
                    <Box sx={{ flex: 1, minWidth: 220, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {isSpanish ? 'Pago total del extracto' : 'Statement full balance'}
                      </Typography>
                      <Typography variant="body2">
                        {isSpanish ? 'A ' : 'To '}<strong>{m.contexto.tarjeta}</strong>
                        {isSpanish ? ' desde ' : ' from '}<strong>{m.contexto.cuenta_origen || '—'}</strong>
                      </Typography>
                    </Box>
                  )}
                  {m.contexto?.tipo === 'cuenta' && (
                    <Box sx={{ flex: 1, minWidth: 220, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {isSpanish ? 'Pago a:' : 'Payment to:'}
                      </Typography>
                      <Typography variant="body2">
                        {m.contexto.nombre}{m.contexto.entidad ? ` — ${m.contexto.entidad}` : ''}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* 24 sep 2026 (Augusto: "si ya esto funciona, entonces no
                    hay necesidad de decir de qué cuenta salió el pago de
                    tarjeta"). Desde que el import empareja los pagos
                    solo, esto llegó acá porque falta la otra mitad --
                    casi siempre porque el extracto de la cuenta todavía
                    no se importó. Conviene decirlo antes de que se ponga
                    a contestar a mano algo que se resuelve solo. */}
                {esFaltaOrigen && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    {isSpanish
                      ? 'Si todavía no importaste el estado de cuenta del banco de donde salió este pago, no hace falta que contestes: al importarlo, PCM lo reconoce y los une solo.'
                      : "If you haven't imported the statement of the account this payment came from, you don't need to answer: importing it will match and merge them automatically."}
                  </Typography>
                )}

                {/* 24 sep 2026 (Augusto: "colocar como pendiente en el caso
                    se piense que es un cambio de moneda"). Salió plata de
                    una cuenta y entró en otra, en otra moneda y con
                    fechas cercanas. PCM no tiene tipo de cambio para
                    confirmarlo, así que no decide: mientras tanto los DOS
                    lados quedan fuera de los totales, porque contarlos
                    como gasto e ingreso reales sería inventar un
                    movimiento en cada dirección. */}
                {esPosibleCambio && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    {isSpanish
                      ? 'Los dos movimientos están fuera de tus totales hasta que decidas. Si eran el mismo dinero cambiado de moneda, únelos; si no tienen nada que ver, sepáralos y cada uno vuelve a contar.'
                      : 'Both movements are out of your totals until you decide. If they were the same money exchanged, merge them; if they are unrelated, separate them and each one counts again.'}
                  </Typography>
                )}

                {esPagoProgramado && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    {isSpanish
                      ? 'PCM lo preparó porque hoy es tu día de pago de tarjetas. No cambia tus saldos ni tu deuda hasta que confirmes. Si pagaste otro monto, corrígelo antes de confirmar.'
                      : "PCM prepared this because today is your card payment day. It won't change your balances or debt until you confirm. If you paid a different amount, fix it before confirming."}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  {esPagoProgramado ? (
                    <>
                      <TextField
                        size="small"
                        type="number"
                        sx={{ width: 170 }}
                        label={isSpanish ? `Monto pagado (${m.moneda === 'USD' ? 'US$' : 'S/'})` : `Amount paid (${m.moneda})`}
                        value={montoPagado[m.id] ?? String(m.monto ?? '')}
                        onChange={(e) => setMontoPagado((p) => ({ ...p, [m.id]: e.target.value }))}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        disabled={resolviendo === m.id || (montoPagado[m.id] !== undefined && !(parseFloat(montoPagado[m.id]) > 0))}
                        onClick={() => resolver(m, 'confirmar_pago')}
                      >
                        {isSpanish ? 'Sí, se pagó' : 'Yes, it was paid'}
                      </Button>
                      <Button
                        size="small"
                        disabled={resolviendo === m.id}
                        onClick={() => resolver(m, 'descartar')}
                      >
                        {isSpanish ? 'No se pagó este mes' : 'Not paid this month'}
                      </Button>
                    </>
                  ) : esPosibleCambio ? (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={resolviendo === m.id}
                        onClick={() => resolver(m, 'cambio_moneda')}
                      >
                        {isSpanish ? 'Sí, es un cambio de moneda' : 'Yes, it is a currency exchange'}
                      </Button>
                      <Button
                        size="small"
                        disabled={resolviendo === m.id}
                        onClick={() => resolver(m, 'separar')}
                      >
                        {isSpanish ? 'No, son movimientos distintos' : 'No, they are unrelated'}
                      </Button>
                    </>
                  ) : esFaltaOrigen ? (
                    <>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id={`origen-${m.id}`}>
                          {isSpanish ? '¿De qué cuenta salió?' : 'Which account did it come from?'}
                        </InputLabel>
                        <Select
                          labelId={`origen-${m.id}`}
                          label={isSpanish ? '¿De qué cuenta salió?' : 'Which account did it come from?'}
                          value={origenElegido[m.id] || ''}
                          onChange={(e) => setOrigenElegido((p) => ({ ...p, [m.id]: e.target.value }))}
                        >
                          {cuentas
                            .filter((c) => c.id !== m.contexto?.id)
                            .map((c) => (
                              <MenuItem key={c.id} value={c.id}>
                                {c.nombre}{c.moneda ? ` (${c.moneda})` : ''}
                              </MenuItem>
                            ))}
                          {/* 28 sep 2026 (Augusto: "añadir la opción de
                              añadir cuenta nueva, sea deuda o cuenta
                              bancaria"). value="" para que elegirlo no
                              deje seleccionada una cuenta inexistente si
                              el usuario cancela el diálogo. */}
                          <MenuItem
                            value=""
                            onClick={() => abrirNuevaCuenta(m)}
                            sx={{ fontStyle: 'italic' }}
                          >
                            + {isSpanish ? 'Crear cuenta nueva...' : 'Create new account...'}
                          </MenuItem>
                        </Select>
                      </FormControl>
                      {/* 25 sep 2026 (Augusto: "estoy tratando de meter
                          este pago de 3771 USD de mi cuenta en dólares y
                          me dice que BBVA está en Soles").
                          Este campo aparece SOLO cuando la cuenta elegida
                          está en otra moneda que el pago. Pagar dólares
                          desde una cuenta en dólares no pide nada, por más
                          que la tarjeta figure registrada en soles: la
                          moneda que manda es la del movimiento. */}
                      {(() => {
                        const origen = cuentas.find(
                          (c) => String(c.id) === String(origenElegido[m.id])
                        );
                        const monedaOrigen = (origen?.moneda || '').toUpperCase();
                        const monedaPago = (m.moneda || '').toUpperCase();
                        const necesita = monedaOrigen && monedaPago && monedaOrigen !== monedaPago;
                        if (!necesita) return null;
                        return (
                          <TextField
                            size="small"
                            type="number"
                            sx={{ width: 190 }}
                            label={
                              isSpanish
                                ? `¿Cuánto salió en ${monedaOrigen}?`
                                : `How much left in ${monedaOrigen}?`
                            }
                            value={montoOrigen[m.id] || ''}
                            onChange={(e) =>
                              setMontoOrigen((p) => ({ ...p, [m.id]: e.target.value }))
                            }
                          />
                        );
                      })()}
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!origenElegido[m.id] || resolviendo === m.id}
                        onClick={() => resolver(m, 'origen')}
                      >
                        {isSpanish ? 'Registrar como transferencia' : 'Record as transfer'}
                      </Button>
                      {/* 21 sep 2026 (Augusto: "estas son devoluciones que
                          me hizo el proveedor de donde compré. No puedo
                          registrarlo"). No todo lo que entra a una tarjeta
                          es un pago tuyo: una devolución del comercio
                          también baja el saldo, y no salió de ninguna
                          cuenta. Sin esta salida, la única forma de sacar
                          esas filas de la lista era borrarlas -- perdiendo
                          un movimiento real. */}
                      <Button
                        size="small"
                        disabled={resolviendo === m.id}
                        onClick={() => resolver(m, 'aceptar')}
                      >
                        {isSpanish ? 'No salió de una cuenta (devolución)' : "Didn't come from an account (refund)"}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      disabled={resolviendo === m.id}
                      onClick={() => resolver(m, 'aceptar')}
                    >
                      {isSpanish ? 'Es un movimiento real' : 'It is a real transaction'}
                    </Button>
                  )}
                  {/* No se ofrece borrar en un posible cambio: borrar un
                      lado dejaría el otro colgado y sin explicación. */}
                  {!esPosibleCambio && !esPagoProgramado && (
                    <Button
                      size="small"
                      color="error"
                      disabled={resolviendo === m.id}
                      onClick={() => resolver(m, 'descartar')}
                    >
                      {isSpanish ? 'Borrarlo' : 'Delete it'}
                    </Button>
                  )}
                </Box>
              </Paper>
            );
          })}
        </>
      )}
      {/* 28 sep 2026: alta rápida de cuenta o deuda sin salir de acá. */}
      <Dialog open={openNuevaCuenta} onClose={() => setOpenNuevaCuenta(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{isSpanish ? 'Nueva cuenta o deuda' : 'New account or debt'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              autoFocus
              size="small"
              label={isSpanish ? 'Nombre' : 'Name'}
              placeholder={isSpanish ? 'BCP Ahorros USD' : 'BCP Savings USD'}
              value={nuevaCuenta.nombre}
              onChange={(e) => setNuevaCuenta((p) => ({ ...p, nombre: e.target.value }))}
            />
            <TextField
              size="small"
              label={isSpanish ? 'Banco o entidad' : 'Bank or institution'}
              placeholder="BCP"
              value={nuevaCuenta.entidad}
              onChange={(e) => setNuevaCuenta((p) => ({ ...p, entidad: e.target.value }))}
            />
            <FormControl size="small">
              <InputLabel id="nueva-tipo">{isSpanish ? 'Tipo' : 'Type'}</InputLabel>
              <Select
                labelId="nueva-tipo"
                label={isSpanish ? 'Tipo' : 'Type'}
                value={nuevaCuenta.tipo}
                onChange={(e) => setNuevaCuenta((p) => ({ ...p, tipo: e.target.value }))}
              >
                <ListSubheader>{isSpanish ? 'Dinero que tienes' : 'Money you have'}</ListSubheader>
                {TIPOS_NUEVA_CUENTA.filter((t) => !esTipoDeuda(t.value)).map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
                <ListSubheader>{isSpanish ? 'Dinero que debes' : 'Money you owe'}</ListSubheader>
                {TIPOS_NUEVA_CUENTA.filter((t) => esTipoDeuda(t.value)).map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel id="nueva-moneda">{isSpanish ? 'Moneda' : 'Currency'}</InputLabel>
              <Select
                labelId="nueva-moneda"
                label={isSpanish ? 'Moneda' : 'Currency'}
                value={nuevaCuenta.moneda}
                onChange={(e) => setNuevaCuenta((p) => ({ ...p, moneda: e.target.value }))}
              >
                <MenuItem value="PEN">PEN</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              label={
                !nuevaCuenta.tipo
                  ? (isSpanish ? 'Monto (opcional)' : 'Amount (optional)')
                  : esTipoDeuda(nuevaCuenta.tipo)
                    ? (isSpanish ? 'Monto que debes (opcional)' : 'Amount owed (optional)')
                    : (isSpanish ? 'Saldo actual (opcional)' : 'Current balance (optional)')
              }
              value={nuevaCuenta.saldo}
              onChange={(e) => setNuevaCuenta((p) => ({ ...p, saldo: e.target.value }))}
            />
            <Typography variant="caption" color="text.secondary">
              {isSpanish
                ? 'Puedes dejarlo en cero y cuadrarlo después importando el extracto o desde Conciliación.'
                : 'You can leave it at zero and reconcile later by importing the statement or from Reconciliation.'}
            </Typography>
            {/* Decir a dónde va a parar evita la sorpresa de crearla y no
                encontrarla después. */}
            {nuevaCuenta.tipo && (
              <Typography variant="caption" color="text.secondary">
                {esTipoDeuda(nuevaCuenta.tipo)
                  ? (isSpanish ? 'Aparecerá en Deudas.' : 'It will appear under Debts.')
                  : (isSpanish ? 'Aparecerá en Cuentas.' : 'It will appear under Accounts.')}
              </Typography>
            )}
            {errorNuevaCuenta && <Alert severity="error">{errorNuevaCuenta}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNuevaCuenta(false)} disabled={creandoCuenta}>
            {isSpanish ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button
            onClick={crearCuenta}
            variant="contained"
            disabled={creandoCuenta || !nuevaCuenta.nombre.trim() || !nuevaCuenta.tipo}
          >
            {creandoCuenta ? (isSpanish ? 'Creando...' : 'Creating...') : (isSpanish ? 'Crear' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RevisionPage;
